<template>
  <div class="import-form-ui">
    <InputFile
      accept=".csv"
      :value="file"
      :reset-trigger="inputResetTrigger"
      :disabled="isLoading"
      @select="handleSelect"
    />
    <div v-show="shouldShowStats" class="import-form-ui__stats">
      <VCard class="flat">
        <div class="import-form-ui__stats-content">
          <ul class="import-form-ui__stats-list">
            <li class="import-form-ui__stats-list-item">
              <span class="import-form-ui__stats-title">Processed:</span>
              <span class="import-form-ui__stats-value"
                ><VNumberCounter :value="processedRowsCount"
              /></span>
            </li>
            <li class="import-form-ui__stats-list-item">
              <span class="import-form-ui__stats-title">Added: </span>
              <span class="import-form-ui__stats-value"
                ><VNumberCounter :value="stats.added"
              /></span>
            </li>
            <li class="import-form-ui__stats-list-item">
              <span class="import-form-ui__stats-title">Updated: </span>
              <span class="import-form-ui__stats-value"
                ><VNumberCounter :value="stats.updated"
              /></span>
            </li>
            <li class="import-form-ui__stats-list-item">
              <span class="import-form-ui__stats-title">Invalid: </span>
              <span class="import-form-ui__stats-value"
                ><VNumberCounter :value="stats.invalid"
              /></span>
            </li>
            <li class="import-form-ui__stats-list-item">
              <span class="import-form-ui__stats-title">Failed: </span>
              <span class="import-form-ui__stats-value"
                ><VNumberCounter :value="stats.failed"
              /></span>
            </li>
          </ul>
          <p :class="['import-form-ui__status', hasError && 'import-form-ui__status--error']">
            {{ status }}<LoadingDots v-if="isLoading" />
          </p>
        </div>
      </VCard>
    </div>
    <div class="import-form-ui__controls">
      <div class="import-form-ui__control">
        <VBtn class="full-width" :disabled="isControlDisabled" @click="onSubmit">Send</VBtn>
      </div>
      <div class="import-form-ui__control">
        <VBtn class="full-width" :disabled="isControlDisabled" outline @click="handleReset">
          Reset
        </VBtn>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { parse } from 'papaparse';

  // Types
  import type {
    TImportResponse,
    TBaseImportRow,
    TImportParser,
    TImportParserStepResult,
    TImportStats,
    IImportModel,
    TImportColumnIndexes,
    EMappingVariant,
    TImportValidationError,
    TImportRequestErrors,
  } from '~/shared/import/types';
  import { EInputStatus } from '~/shared/import/types';

  // Config
  import { CSV_ROW_GENERATOR, DEFAULT_IMPORT_STATS } from '~/shared/import/config';

  // Components
  import VBtn from '~/misc/v-btn';
  import VCard from '~/misc/v-card';
  import InputFile from '~/misc/input/file/InputFile.vue';
  import VNumberCounter from '~/misc/v-number-counter';
  import LoadingDots from '~/misc/loading-dots/LoadingDots.vue';

  const props = defineProps({
    resolver: {
      type: Object as PropType<IImportModel>,
      required: true,
    },
    variant: {
      type: String as PropType<EMappingVariant>,
      required: true,
    },
    headerIndexes: {
      type: Object as PropType<TImportColumnIndexes>,
      required: true,
    },
    selectedSourceName: {
      type: String as PropType<string | null>,
      default: null,
    },
    linesToSkipCount: {
      type: Number,
      default: 0,
    },
    isSubmitBlocked: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits<{
    (e: 'file-selected', file: File | null): void;
    (e: 'on-validation-error', errors: TImportValidationError[]): void;
    (e: 'on-request-error', errors: TImportRequestErrors): void;
    (e: 'on-submit'): void;
  }>();

  const file: Ref<File | null> = ref<File | null>(null);
  const status: Ref<string> = ref<string>('');
  const count: Ref<number> = ref<number>(0);
  const chunk: Ref<string[][]> = ref<string[][]>([]);
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const isParsed: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);
  const processedRowsCount: Ref<number> = ref<number>(0);
  const shouldShowStats: Ref<boolean> = ref<boolean>(false);
  const stats: Ref<TImportStats> = ref<TImportStats>({
    added: 0,
    updated: 0,
    invalid: 0,
    failed: 0,
  });
  const inputResetTrigger: Ref<number> = ref<number>(0);
  const skippedRowsCount: Ref<number> = ref<number>(0);

  const isControlDisabled: ComputedRef<boolean> = computed<boolean>(
    () => !file.value || isLoading.value || props.isSubmitBlocked
  );

  const setFile = (data: File | null) => {
    file.value = data;
  };

  const setStatus = (value: string) => {
    status.value = value;
  };

  const setCount = (value: number) => {
    count.value = value;
  };

  const setChunk = (data: string[][]) => {
    chunk.value = data;
  };

  const setIsLoading = (value: boolean) => {
    isLoading.value = value;
  };

  const setIsParsed = (value: boolean) => {
    isParsed.value = value;
  };

  const setHasError = (value: boolean) => {
    hasError.value = value;
  };

  const setProcessedRowsCount = (value: number) => {
    processedRowsCount.value = value;
  };

  const setShouldShowStats = (value: boolean) => {
    shouldShowStats.value = value;
  };

  const setSkippedRowsCount = (value: number) => {
    skippedRowsCount.value = value;
  };

  const setStats = (data: TImportStats) => {
    stats.value = { ...data };
  };

  const triggerInputReset = () => {
    inputResetTrigger.value++;
  };

  const handleReset = () => {
    selectFile(null);
    setCount(0);
    setChunk([]);
    setIsLoading(false);
    setIsParsed(false);
    setHasError(false);
    setProcessedRowsCount(0);
    setShouldShowStats(false);
    setStats(DEFAULT_IMPORT_STATS);
    setSkippedRowsCount(0);
    triggerInputReset();
  };

  const handleParse = (receivedFile: File) => {
    parse(receivedFile, {
      skipEmptyLines: true,
      step: async (results: TImportParserStepResult<string[]>, parser: TImportParser) => {
        count.value++;
        chunk.value.push(results.data);

        if (chunk.value.length === 50) {
          await handleSend(parser);
        }
      },
      complete: async () => {
        await nextTick();

        if (hasError.value) return;

        setIsParsed(true);

        await handleSend();
      },
    });
  };

  const handleComplete = () => {
    if (isParsed.value) {
      setStatus(EInputStatus.COMPLETE);
      setIsLoading(false);
    }
  };

  const handleSend = async (parser?: TImportParser) => {
    parser?.pause();

    if (!chunk.value.length) {
      handleComplete();

      return;
    }

    try {
      const rowsToSkip: number = Math.min(
        Math.max(props.linesToSkipCount - skippedRowsCount.value, 0),
        chunk.value.length
      );

      setSkippedRowsCount(skippedRowsCount.value + rowsToSkip);

      const filteredChunk: string[][] = chunk.value.slice(rowsToSkip);

      if (!filteredChunk.length) {
        setChunk([]);

        parser?.resume();

        handleComplete();

        return;
      }

      const rows: TBaseImportRow[] = filteredChunk.map((item: string[], index: number) =>
        CSV_ROW_GENERATOR[props.variant].handle(
          item,
          props.headerIndexes,
          props.selectedSourceName,
          processedRowsCount.value,
          index
        )
      );

      const data: TImportResponse = await props.resolver.process({ rows });

      stats.value.added += data.inserted;
      stats.value.updated += data.updated;
      stats.value.invalid += data.invalid.length;

      processedRowsCount.value += rows.length;

      emit('on-validation-error', data.invalid);

      setChunk([]);

      parser?.resume();

      handleComplete();
    } catch (e) {
      if (e && typeof e === 'object' && 'response' in e) {
        const errorResponse = e as { response: { data: { errors: TImportRequestErrors } } };
        const errors: TImportRequestErrors = errorResponse?.response?.data?.errors;

        stats.value.failed += chunk.value.length;

        errors && emit('on-request-error', errors);

        setChunk([]);

        parser?.resume();

        handleComplete();

        return;
      }

      parser?.abort();

      setStatus(EInputStatus.FAILED);
      setHasError(true);
      setIsLoading(false);
    }
  };

  const onSubmit = () => {
    if (!file.value) return;

    setCount(0);
    setChunk([]);
    setIsParsed(false);
    setStats(DEFAULT_IMPORT_STATS);
    setStatus(EInputStatus.UPLOADING);
    setIsLoading(true);
    setShouldShowStats(true);
    setHasError(false);
    setProcessedRowsCount(0);
    setSkippedRowsCount(0);
    handleParse(file.value);
    emit('on-submit');
  };

  const handleSelect = (file: File | null) => {
    if (!file) {
      handleReset();

      return;
    }

    setShouldShowStats(false);
    selectFile(file);
  };

  const selectFile = (value: File | null) => {
    setFile(value);
    emit('file-selected', value);
  };
</script>
